import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";

export const SIDEBAR_WIDTH = 200;

// Local alpha-white constants
const w06 = "rgba(255,255,255,0.06)";
const w08 = "rgba(255,255,255,0.08)";
const w35 = "rgba(255,255,255,0.35)";
const w45 = "rgba(255,255,255,0.45)";
const w6 = "rgba(255,255,255,0.6)";
const w65 = "rgba(255,255,255,0.65)";
// ── Sidebar shell ─────────────────────────────────────────────────────────────

export const SidebarRoot = styled("div")(({ theme }: { theme: Theme }) => ({
  width: SIDEBAR_WIDTH,
  minWidth: SIDEBAR_WIDTH,
  background: theme.palette.brand.navy,
  display: "flex",
  flexDirection: "column",
  height: "100vh",
  borderRight: `1px solid ${w08}`,
  zIndex: 50,
  position: "relative",
  flexShrink: 0,
}));

export const LogoZone = styled("div")({
  padding: "20px 16px 16px",
  display: "flex",
  alignItems: "center",
  gap: 10,
  borderBottom: `1px solid ${w08}`,
});

export const LogoIcon = styled("div")({
  width: 32,
  height: 32,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

export const LogoSymbol = styled("img")({
  width: 32,
  height: 32,
  objectFit: "contain",
  display: "block",
});

export const LogoName = styled("span")(({ theme }: { theme: Theme }) => ({
  color: theme.palette.brand.white,
  fontWeight: 700,
  fontSize: 17,
  letterSpacing: "-0.02em",
}));

export const SidebarNav = styled("nav")({
  flex: 1,
  padding: "16px 8px",
  display: "flex",
  flexDirection: "column",
  gap: 2,
});

export const MenuItem = styled("button")(({ theme }: { theme: Theme }) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  gap: 10,
  width: "100%",
  padding: "11px 14px",
  background: "transparent",
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
  color: w65,
  fontSize: 13.5,
  fontWeight: 500,
  textAlign: "left" as const,
  transition: "all 0.15s",
  fontFamily: "inherit",
  "& svg": {
    width: 18,
    height: 18,
    flexShrink: 0,
    opacity: 0.55,
    transition: "opacity 0.15s",
  },
  "&:hover": {
    background: theme.palette.brand.navyLight,
    color: theme.palette.brand.coral,
    "& svg": { opacity: 0.9 },
  },
  '&[data-active="true"]': {
    background: theme.palette.brand.navyActive,
    color: theme.palette.brand.white,
    fontWeight: 600,
    "& svg": { opacity: 1 },
  },
}));

export const ArrowWrap = styled("span")({
  marginLeft: "auto",
  opacity: 0.4,
  display: "flex",
  alignItems: "center",
});

export const SidebarBottom = styled("div")({
  padding: "10px 8px 16px",
  borderTop: `1px solid ${w08}`,
  display: "flex",
  flexDirection: "column",
  gap: 2,
});

export const BottomMenuItem = styled(MenuItem)(({ theme }: { theme: Theme }) => ({
  padding: "9px 14px",
  color: w45,
  fontSize: 12.5,
  "& svg": { transition: "none" },
  "&:hover": {
    background: "transparent",
    color: theme.palette.brand.coral,
    "& svg": { opacity: 0.55 },
  },
}));

// ── Flyout panel ──────────────────────────────────────────────────────────────

export const FlyoutPanel = styled("div")(({ theme }: { theme: Theme }) => ({
  position: "fixed",
  left: SIDEBAR_WIDTH,
  top: 0,
  bottom: 0,
  width: 260,
  background: theme.palette.brand.navyDeep,
  borderRight: `1px solid ${w08}`,
  zIndex: 40,
  opacity: 0,
  pointerEvents: "none",
  transform: "translateX(-8px)",
  transition: "opacity 0.2s, transform 0.2s",
  display: "flex",
  flexDirection: "column",
  boxShadow: "8px 0 32px rgba(0,0,0,0.25)",
  '&[data-open="true"]': {
    opacity: 1,
    pointerEvents: "auto",
    transform: "translateX(0)",
  },
}));

export const FlyoutHeader = styled("div")({
  padding: "18px 20px 14px",
  fontSize: 10,
  fontWeight: 700,
  textTransform: "uppercase" as const,
  letterSpacing: "0.1em",
  color: w35,
  borderBottom: `1px solid ${w06}`,
});

export const FlyoutBody = styled("div")({
  flex: 1,
  overflowY: "auto",
  padding: 8,
  "&::-webkit-scrollbar": { width: 4 },
  "&::-webkit-scrollbar-thumb": {
    background: w08,
    borderRadius: 2,
  },
});

export const FlyoutItemEl = styled("div")(({ theme }: { theme: Theme }) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  gap: 9,
  padding: "9px 12px",
  borderRadius: 6,
  cursor: "pointer",
  color: w6,
  fontSize: 13,
  fontWeight: 400,
  transition: "all 0.15s",
  fontFamily: "inherit",
  "& svg": {
    width: 15,
    height: 15,
    flexShrink: 0,
    opacity: 0.5,
    transition: "opacity 0.15s",
  },
  "&:hover": {
    background: w06,
    color: theme.palette.brand.coral,
    "& svg": { opacity: 0.85 },
  },
  '&[data-active="true"]': {
    color: theme.palette.brand.white,
    background: w08,
    fontWeight: 500,
  },
}));
